import { RequestModel } from '@bo-client/shared/models/request.model';
import { AllAreaEnum } from '@bo-client/activation-management/shared/models/commissioningModel';
import { ConnectionObject, ConnectionObjectFormV2 } from '@bo-client/shared/models/connectionObject';
import { ConnectionObjectAdditionalInfoModel } from '@bo-client/activation-management/shared/models/connection-object-additional-info.model';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';
import { ContactDataDto, ContactDataForm, InvoiceContactForm } from '@bo-client/shared/models/contactDataDto';
import { DocumentDtoModel, UploadForm } from '@bo-client/shared/models/documentDto';
import { CombinedForm, TypedForm } from '@bo-client/shared/forms/generic-forms/typedForm.type';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ArticleCase, ArticleGender, articles } from '@bo-client/shared/constants/articles';
import { TenantDto } from '@bo-client/shared/models/tenantDto';
import { ContactData, OfferPosition } from '@shared/models';
import { StatusType } from '@bo-client/core/api/meta-service';
import { BusinessPartner } from '@bo-client/shared/models/businessPartner';

export class ConnectionChangeDto extends RequestModel {
  constructor(type: ConnectionChangeRequestTypeEnum) {
    super();
    this.connectionChangeRequestType = type;
  }

  // Generic Data
  id: number = null;
  externalId: string = null;
  tenant?: TenantDto = { id: null };
  companyLocation?: CompanyLocationDto = { contactData: null, external_id: null, id: null, name: null };
  state: ConnectionChangeRequestStatusEnum | string = null;
  fromDate: string | Date = null;
  toDate: string | Date = null;
  createdAt: string | Date = null;
  createdBy: string = null;
  updatedAt: string | Date = null;
  updatedBy: string = null;
  // Connection Object Data
  connectionObject?: ConnectionObject = {};
  connectionObjectAdditionalInfo?: ConnectionObjectAdditionalInfoModel = {};
  // Contact Data
  customer?: ContactDataDto = {};
  invoiceContact?: ContactDataDto = {};
  // Technical Data
  area: AllAreaEnum = null;
  areas?: AllAreaEnum[] = [];
  connectionChangeRequestType: ConnectionChangeRequestTypeEnum = null;
  uploads?: DocumentDtoModel[] = [];
  installedPower: number = null;
  isEmptyHouse: boolean = null;
  requiredPower: number = null;
  meterNumber: string = null;
  technicalComment: string = null;
  powerIncrease: boolean = null;
  moveServiceEntranceBox: boolean = null;
  moveConnectionLine: boolean = null;
  installedFuse: string = null;
  civilEngineering: CivilEngineeringEnum = null;
  lineChangeComment: string = null;
  // Additional Data
  supplyAppointmentWishDate: Date | string = null;
  isOwner: boolean = null;
  requestFeedbackComment: string = null;
  // Offer Data
  positions?: OfferPosition[] = [];
  totalPriceNetto: number = null;
  additionalOfferText: string = null;
  // Editor-specific Data
  executingCivilEngineeringPartner?: BusinessPartner = null;
  responsibleBuildingAuthority?: BusinessPartner = null;
  lbm?: BusinessPartner = null; // Landesbauamt
  assignee?: ContactData = {};
  executionDate: Date | string = null;
  shortDescription: string = null;
  longDescription: string = null;
  externalCustomerId: string = null;
  inspectionAppointmentComment: string = null;

  /**
   * Generiert den Anzeigenamen für die Anschlussänderung, basierend auf den Anschlussänderungstypen und grammatischen Kasus
   * @param requestType - Anschlussänderungstyp der Änderung
   * @param articleCase - Kasus der Änderung im Satz
   */
  static GET_FORMAL_SUBJECT_NAME = (
    requestType: ConnectionChangeRequestTypeEnum,
    articleCase: ArticleCase
  ) => {
    const subjectType = ConnectionChangeRequestTypeDisplayValues[requestType];
    let subjectName: string;
    if (requestType === ConnectionChangeRequestTypeEnum.TEMPORARY) {
      switch (articleCase) {
        case ArticleCase.nom:
          subjectName = 'temporärer Anschluss';
          break;
        case ArticleCase.gen:
          subjectName = 'temporären Anschlusses';
          break;
        case ArticleCase.dat:
          subjectName = 'temporärem Anschluss';
          break;
        case ArticleCase.acc:
          subjectName = 'temporären Anschluss';
          break;
        default:
          subjectName = 'temporärer Anschluss';
      }
    } else subjectName = subjectType.name;
    return `${articles.upper.formal[subjectType?.gender][articleCase]} ${subjectName}`;
  };
}

export enum ConnectionChangeRequestTypeEnum {
  CHANGE = 'CHANGE',
  DISCONNECTION = 'DISCONNECTION',
  TEMPORARY = 'TEMPORARY'
}

export const ConnectionChangeRequestTypeDisplayValues = {
  [ConnectionChangeRequestTypeEnum.CHANGE]: { name: 'Anschlussänderung', gender: ArticleGender.female },
  [ConnectionChangeRequestTypeEnum.DISCONNECTION]: {
    name: 'Anschlusstrennung',
    gender: ArticleGender.female
  },
  [ConnectionChangeRequestTypeEnum.TEMPORARY]: { name: 'Temporärer Anschluss', gender: ArticleGender.male }
};

export enum ConnectionChangeRequestStatusEnum {
  Incomplete = 'Incomplete',
  Requested = 'Requested',
  Error = 'Error',
  Expired = 'Expired',
  FeedbackNeeded = 'FeedbackNeeded',
  OfferSent = 'OfferSent',
  Ordered = 'Ordered'
}

export const ConnectionChangeRequestStatusDisplayNames = {
  [ConnectionChangeRequestStatusEnum.Incomplete]: 'Unvollständig',
  [ConnectionChangeRequestStatusEnum.Requested]: 'Angefragt',
  [ConnectionChangeRequestStatusEnum.Error]: 'Fehler',
  [ConnectionChangeRequestStatusEnum.Expired]: 'Abgelaufen',
  [ConnectionChangeRequestStatusEnum.FeedbackNeeded]: 'Feedback benötigt',
  [ConnectionChangeRequestStatusEnum.OfferSent]: 'Angebot gesendet',
  [ConnectionChangeRequestStatusEnum.Ordered]: 'Bestellt'
};

export const ConnectionChangeRequestStatusColors = {
  [ConnectionChangeRequestStatusEnum.Incomplete]: '#f0b429',
  [ConnectionChangeRequestStatusEnum.Requested]: '#71dde3',
  [ConnectionChangeRequestStatusEnum.Error]: '#fd7e14',
  [ConnectionChangeRequestStatusEnum.FeedbackNeeded]: '#f0b429',
  [ConnectionChangeRequestStatusEnum.OfferSent]: '#C3E88D',
  [ConnectionChangeRequestStatusEnum.Ordered]: '#e99aa6'
};

export const ConnectionChangeRequestStatusConstant: StatusType[] = Object.values(
  ConnectionChangeRequestStatusEnum
).map((status) => ({
  value: status,
  displayName: ConnectionChangeRequestStatusDisplayNames[status],
  cssColor: ConnectionChangeRequestStatusColors[status]
}));

export enum CivilEngineeringEnum {
  YES = 'YES',
  PUBLIC_ONLY = 'PUBLIC_ONLY',
  NO = 'NO'
}

export const CivilEngineeringDisplayNames = {
  [CivilEngineeringEnum.YES]: 'Mit allen Tiefbauarbeiten',
  [CivilEngineeringEnum.PUBLIC_ONLY]: 'Ohne Tiefbauarbeiten im privaten Bereich',
  [CivilEngineeringEnum.NO]: 'Ohne Tiefbauarbeiten'
};

type BaseForm = TypedForm<
  Pick<
    ConnectionChangeDto,
    | 'area'
    | 'connectionChangeRequestType'
    | 'connectionObjectAdditionalInfo'
    | 'isOwner'
    | 'installedPower'
    | 'isEmptyHouse'
    | 'requiredPower'
    | 'meterNumber'
    | 'technicalComment'
    | 'powerIncrease'
    | 'moveServiceEntranceBox'
    | 'moveConnectionLine'
    | 'civilEngineering'
    | 'installedFuse'
    | 'externalId'
    | 'externalCustomerId'
    | 'shortDescription'
    | 'longDescription'
    | 'lineChangeComment'
    | 'inspectionAppointmentComment'
    // Complex Form
    // | 'connectionObject'
    // | 'customer'
    // | 'invoiceContact'
    // | 'uploads'
    // | 'fromDate'
    // | 'toDate'
    // | 'lbm'
    // | 'responsibleBuildingAuthority'
    // | 'executingCivilEngineeringPartner'
    // | 'executionDate'
    // | 'areas'
    // | 'supplyAppointmentWishDate'
  >
>;

type ComplexForm = FormGroup<{
  connectionObject: ConnectionObjectFormV2;
  customer: ContactDataForm;
  invoiceContact: InvoiceContactForm;
  uploads: FormArray<UploadForm>;
  fromDate: FormControl<Date>;
  toDate: FormControl<Date>;
  lbm: FormControl<BusinessPartner>;
  responsibleBuildingAuthority: FormControl<BusinessPartner>;
  executingCivilEngineeringPartner: FormControl<BusinessPartner>;
  executionDate: FormControl<Date>;
  areas: FormControl<AllAreaEnum[]>;
  supplyAppointmentWishDate: FormControl<Date>;
  // Nicht-DTO/transiente Felder
  differingInvoiceContact: FormControl<boolean>;
}>;

export type ConnectionChangeForm = CombinedForm<BaseForm, ComplexForm>;
